<script setup lang="ts">
import type { IconKeys } from "@/components/ui/Icon/Icon.interface"
import { DialogClose, DialogContent, DialogDescription, DialogPortal, DialogRoot, DialogTitle } from "reka-ui"
import { watch } from "vue"
import { Button, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"

interface DrawerRekaProps {
  open: boolean
  title?: string
  titleIcon?: IconKeys
}

const props = withDefaults(defineProps<DrawerRekaProps>(), { title: "", titleIcon: undefined })

const emit = defineEmits<{
  "update:open": [value: boolean]
  "open": []
  "close": []
}>()

const { t } = useTranslation()

watch(
  () => props.open,
  (val) => {
    val ? emit("open") : emit("close")
  },
)

function onOpenChange(val: boolean) {
  emit("update:open", val)
}
</script>

<template>
  <Teleport to="body">
    <Transition name="drawer-overlay">
      <div
        v-if="open"
        class="fixed inset-0 z-50 bg-black/40"
        aria-hidden="true"
      />
    </Transition>
  </Teleport>
  <DialogRoot :open="open" @update:open="onOpenChange">
    <DialogPortal>
      <Transition name="drawer-panel">
        <DialogContent
          v-if="open"
          class="fixed bottom-0 left-0 right-0 w-full z-50 bg-white border-t border-gray-200 rounded-t-xl flex flex-col h-full max-h-[75vh] overflow-y-auto outline-none md:inset-y-0 md:left-auto md:w-115 md:max-w-[95vw] md:max-h-full md:rounded-none md:border-l md:border-t-0"
          @open-auto-focus.prevent
        >
          <DialogTitle v-if="$slots.header" as="h2" class="sr-only" data-test="drawer_title">
            {{ title }}
          </DialogTitle>
          <DialogDescription class="sr-only">
            {{ title }}
          </DialogDescription>
          <slot name="header">
            <div class="flex justify-between items-center min-h-12 pt-4 px-6 mb-6 sticky top-0 bg-white text-primary">
              <div class="flex items-center gap-2 font-heading text-2xl">
                <slot name="titleIcon">
                  <Icon v-if="titleIcon" :icon="titleIcon" />
                </slot>
                <DialogTitle as="h2" class="line-clamp-1 text-ellipsis" data-test="drawer_title">
                  {{ title }}
                </DialogTitle>
              </div>
              <DialogClose as-child>
                <Button variant="flat" size="xs" data-test="drawer-close">
                  <span class="sr-only">{{ t('close') }}</span>
                  <Icon icon="Close" />
                </Button>
              </DialogClose>
            </div>
          </slot>

          <div class="grow overflow-y-auto px-6">
            <slot />
          </div>

          <slot name="footer" />
        </DialogContent>
      </Transition>
    </DialogPortal>
  </DialogRoot>
</template>
